import { useEffect, useState } from "react";

//STYLES
import styles from "./NavBar.module.scss";

//UI COMPONENTS
import { NavbarSkeleton } from "./ui/NavbarSkeleton";
import AccountsSelectorMobile from "../AccountsSelectorMobile/AccountsSelectorMobile";
import NavItems from "./ui/NavItems";
import Skeleton from "../Skeleton/Skeleton";
import OptionsBottomSheet from "../OptionsBottomSheet/OptionsBottomSheet";

//ASSETS
import allariaLogo from "../../assets/images/icons/navbar/navbar-allaria-desktop-logo.svg";
import MenuIcon from "../../assets/images/icons/navbar/navbar-menu-mobile-icon.svg?react";
import userIcon from "../../assets/images/icons/navbar/navbar-user-icon.svg";
import HomeIcon from "../../assets/images/icons/navbar/navbar-home-icon.svg?react";
import OperateIcon from "../../assets/images/icons/navbar/navbar-operate-icon.svg?react";
import ActivityIcon from "../../assets/images/icons/navbar/navbar-activity-icon.svg?react";
import GoToAllariaMasIcon from "../../assets/images/icons/navbar/navbar-go-to-allariamas-icon.svg?react";
import SwitchAccountIcon from "../../assets/images/icons/navbar/navbar-switch-account-icon.svg?react";
import FavoritesIcon from "../../assets/images/icons/navbar/nav-var-favorites-icon.svg?react";
import LogOutIcon from "../../assets/images/icons/logout-icon.svg?react";

//UTILS
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { PATH_APP } from "../../routes/paths";
import { logout } from "../../store/slices/authSlice";
import { openOperationMEPModal } from "../../store/slices/operationMEPSlice";
import { getAllariaMasRedirectUrl } from "../../connectors/unifyApi";
import { onErrorGoToAllariaMas } from "../../utils";
import { AxiosError } from "axios";
import { useAppSelector } from "../../store/hooks";
import { openExternal } from "./utils/utils";
import { openCryptoModal } from "../../store/slices/cryptoSlice";
import useFeatures from "../../hooks/useFeatures";
import { isIpad, logDeviceAndViewport } from "../../utils/deviceInfo";
import { ENABLE_NAVBAR_DRAWER_LOGS } from "../../constants/logging";
// import {
//   openModal,
//   updateAction,
// } from "../../store/slices/unifiedBalanceSlice";

const Navbar = ({ loading = false }: { loading?: boolean }) => {
  const {
    accountContainer,
    desktop,
    drawer,
    drawerAccountSelector,
    drawerBackground,
    drawerClose,
    drawerHeader,
    drawerItems,
    headerContainer,
    ipad,
    itemBorder,
    itemSelected,
    mirrorAccountContainer,
    mobile,
    mobileBottomBarContent,
    selectorContainer,
    toolbar,
    userIconStyle,
  } = styles;

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isOpenAccountSelectorMobile, setIsOpenAccountSelectorMobile] =
    useState(false);
  const [isOpenAccountBottomSheet, setIsOpenAccountBottomSheet] =
    useState(false);
  const [isIpadDevice, setIsIpadDevice] = useState(false);

  useEffect(() => {
    let cancelled = false;
    isIpad().then((result) => {
      if (!cancelled) setIsIpadDevice(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMobileDrawer = () => {
    window.scrollTo({ top: 0, left: 0 });
    setMobileDrawerOpen((prevState) => !prevState);
  };

  useEffect(() => {
    if (!mobileDrawerOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    if (ENABLE_NAVBAR_DRAWER_LOGS) {
      logDeviceAndViewport("[navbar-drawer-open]");
    }
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileDrawerOpen]);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.authentication);
  const { hasAccepted } = useAppSelector((state) => state.termsAndConditions);
  const { isMepEnabled } = useFeatures();

  //Crypto
  const isCryptoOnly =
    user?.authorization?.crypto?.roles?.includes("CRYPTO_ONLY");
  const isCryptoUser = user?.authorization?.crypto?.roles?.includes("VIEW_ALL");

  const pathSelected = location.pathname;

  const handleNavigate = (path: string) => {
    if (mobileDrawerOpen) {
      toggleMobileDrawer();
    }

    navigate(path);
  };

  const handleFeedbackButtonClicked = () => {
    navigate(
      {
        pathname: "ayuda",
        search: createSearchParams({ question: "testing_phase" }).toString(),
        hash: "testing_phase",
      },
      { replace: true },
    );

    if (mobileDrawerOpen) toggleMobileDrawer();
  };

  const handleMEPClick = () => {
    dispatch(openOperationMEPModal());
    if (mobileDrawerOpen) {
      toggleMobileDrawer();
    }
  };

  const handlegoToAllariaMas = async () => {
    try {
      const response = await getAllariaMasRedirectUrl();

      if (response.url) {
        openExternal(response.url, "_blank");
      } else {
        onErrorGoToAllariaMas();
      }
    } catch (error) {
      const errorCode =
        error && error instanceof AxiosError
          ? error?.response?.data?.code
          : null;

      onErrorGoToAllariaMas(errorCode);
    }
  };

  const handleGoToAllariaDigital = () => {
    dispatch(openCryptoModal());
  };

  const { allariaMasLinkedAccount } = useAppSelector(
    (state) => state.authentication,
  );
  const { accountSelected } = useAppSelector((state) => state.userAccounts);

  const isSelectedPath = (path: string) => {
    return pathSelected === path;
  };

  const menuItems: {
    path: string;
    text: string;
    border: boolean;
    translate?: boolean;
    isDisabled?: boolean;
    onClick?: () => void;
  }[] = [
    {
      path: PATH_APP.home,
      text: "Inicio",
      border: true,
    },
    {
      path: PATH_APP.transactions,
      text: "Transferencias",
      border: true,
    },
    {
      path: PATH_APP.valuation,
      text: "Valuación de cartera",
      border: false,
    },
    {
      path: PATH_APP.returns,
      text: "Evolución de cartera",
      border: false,
    },
    {
      path: PATH_APP.reports,
      text: "Informes",
      border: true,
    },
    {
      path: PATH_APP.research,
      text: "Research",
      border: true,
    },
    {
      path: "",
      text: "Dólar MEP",
      border: true,
      translate: false,
      onClick: handleMEPClick,
      isDisabled: !isMepEnabled,
    },
    {
      path: PATH_APP.help,
      text: "Ayuda",
      border: false,
    },
  ];

  const bottomBarItems = [
    {
      path: PATH_APP.home,
      text: "Inicio",
      icon: <HomeIcon />,
      class: "",
      action: () => handleNavigate(PATH_APP.home),
    },
    {
      path: PATH_APP.activity,
      text: "Actividad",
      icon: <ActivityIcon />,
      class: "",
      action: () => handleNavigate(PATH_APP.activity),
    },
    {
      path: PATH_APP.market,
      text: "Operar",
      icon: <OperateIcon />,
      class: "",
      action: () => handleNavigate(PATH_APP.market),
    },
    {
      path: PATH_APP.favorites,
      text: "Favoritos",
      icon: <FavoritesIcon />,
      class: "",
      action: () => handleNavigate(PATH_APP.favorites),
    },
  ];

  const accountBottomSheetOptions = [
    {
      key: "PROFILE",
      label: "Mi perfil",
      onClick: () => {
        navigate(PATH_APP.profile);
        setIsOpenAccountBottomSheet(false);
      },
    },
    ...(allariaMasLinkedAccount
      ? [
          {
            key: "ALLARIA+",
            label: "Ir a Allaria+",
            icon: <LogOutIcon />,
            onClick: () => {
              handlegoToAllariaMas();
              setIsOpenAccountBottomSheet(false);
            },
          },
        ]
      : []),
    // {
    //   key: "UNIFIED_BALANCE",
    //   label: "Saldo unificado",
    //   onClick: () => {
    //     dispatch(openModal());
    //     dispatch(updateAction("UNIFIED_ACCOUNTS_DATA"));
    //   },
    // },
    {
      key: "LOGOUT",
      label: "Cerrar sesión",
      onClick: () => {
        dispatch(logout());
        setIsOpenAccountBottomSheet(false);
      },
    },
  ];

  return (
    <>
      {/* Desktop nav */}
      <div className={desktop}>
        <div
          className={headerContainer}
          onClick={() => handleNavigate(PATH_APP.home)}
        >
          <img src={allariaLogo} alt="Allaria Logo" />
        </div>
        {loading ? <NavbarSkeleton /> : <NavItems />}
      </div>

      {/* Mobile nav */}
      <div className={mobile}>
        <div className={toolbar}>
          <div onClick={() => handleNavigate(PATH_APP.home)}>
            <img src={allariaLogo} alt="Allaria Logo" />
          </div>
          {hasAccepted == true && (
            <button
              type="button"
              onClick={() => setIsOpenAccountBottomSheet(true)}
              className={userIconStyle}
            >
              <img src={userIcon} alt="User icon" />
            </button>
          )}
        </div>

        <div
          className={`${drawer} ${isIpadDevice ? ipad : ""} ${!mobileDrawerOpen ? drawerClose : ""}`}
        >
          <div className={drawerItems}>
            <div className={drawerHeader}></div>
            <div className={drawerAccountSelector}>
              {!accountSelected ? (
                <Skeleton width="70%" height="25px" />
              ) : (
                <div className={accountContainer}>
                  <p>{accountSelected?.name}</p>
                  <span>#{accountSelected?.number}</span>
                </div>
              )}

              <div className={selectorContainer}>
                {!!accountSelected &&
                  [
                    "ONBOARDING_IN_PROCESS",
                    "IN_PROCESS",
                    "ACCOUNT_CREATION_PENDING",
                  ].includes(accountSelected?.unifyState) && (
                    <div className={mirrorAccountContainer}>
                      Unificación en proceso
                    </div>
                  )}
                {!!accountSelected &&
                  accountSelected?.unifyState === "MIRRORED" && (
                    <div className={mirrorAccountContainer}>Unificada</div>
                  )}

                {!!accountSelected && !accountSelected.inSinglAccount && (
                  <button
                    type="button"
                    onClick={() => setIsOpenAccountSelectorMobile(true)}
                  >
                    <SwitchAccountIcon />
                  </button>
                )}
              </div>
            </div>

            <ul>
              {menuItems.map((item) => {
                return (
                  <li
                    key={item.text}
                    className={`${
                      isSelectedPath(item.path) ? itemSelected : ""
                    } ${item.border ? itemBorder : ""}`}
                  >
                    <button
                      type="button"
                      translate={
                        item.translate === true || item.translate === undefined
                          ? "yes"
                          : "no"
                      }
                      onClick={() =>
                        item.onClick
                          ? item.onClick()
                          : handleNavigate(item.path)
                      }
                      disabled={!!item.isDisabled}
                    >
                      {item.text}
                    </button>
                  </li>
                );
              })}

              {allariaMasLinkedAccount && (
                <li>
                  <button type="button" onClick={() => handlegoToAllariaMas()}>
                    Ir a Allaria+ <GoToAllariaMasIcon />
                  </button>
                </li>
              )}

              {(isCryptoUser || isCryptoOnly) && (
                <li>
                  <button
                    type="button"
                    onClick={() => handleGoToAllariaDigital()}
                  >
                    Ir a Allaria Digital <GoToAllariaMasIcon />
                  </button>
                </li>
              )}

              <li>
                <button type="button" onClick={handleFeedbackButtonClicked}>
                  Dar feedback
                </button>
              </li>

              <li>
                <button
                  type="button"
                  onClick={() => {
                    dispatch(logout());
                  }}
                >
                  Cerrar sesión
                </button>
              </li>
            </ul>
          </div>
          <div className={drawerBackground} onClick={toggleMobileDrawer} />
        </div>
        <AccountsSelectorMobile
          isOpen={isOpenAccountSelectorMobile}
          handleClose={() => setIsOpenAccountSelectorMobile(false)}
        />
        <OptionsBottomSheet
          open={isOpenAccountBottomSheet}
          onClose={() => setIsOpenAccountBottomSheet(false)}
          options={accountBottomSheetOptions}
        />

        {/** navbar */}
        <nav className={`${mobileBottomBarContent}`}>
          <ul>
            {bottomBarItems.map((item) => {
              return (
                <li
                  key={item.text}
                  className={`${
                    isSelectedPath(item.path) ? itemSelected : ""
                  } ${item.class}`}
                >
                  <button type="button" onClick={item.action}>
                    {item.icon}
                    <span>{item.text}</span>
                  </button>
                </li>
              );
            })}
            <li className={`${mobileDrawerOpen ? itemSelected : ""}`}>
              <button type="button" onClick={toggleMobileDrawer}>
                <MenuIcon />
                <span>Menú</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};

export default Navbar;
